import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PaperPlaneRight, Plus, Paperclip, X, FilePdf, DownloadSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = { open: "Aperto", in_progress: "In lavorazione", resolved: "Risolto", closed: "Chiuso" };
const STATUS_COLOR = {
  open: "bg-orange-100 text-orange-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-green-100 text-green-700",
  closed: "bg-gray-100 text-gray-700",
};
const PRIORITY_LABEL = { low: "Bassa", normal: "Normale", high: "Alta" };
const ALLOWED = ["image/png","image/jpeg","image/jpg","image/gif","image/webp","application/pdf"];
const MAX_SIZE = 10 * 1024 * 1024;

export const formatSize = (b) => b < 1024 ? `${b}B` : b < 1024*1024 ? `${(b/1024).toFixed(1)}KB` : `${(b/1024/1024).toFixed(1)}MB`;

export function FilePicker({ files, setFiles, testId = "file-picker" }) {
  const inputRef = useRef(null);
  const onPick = (e) => {
    const fl = Array.from(e.target.files || []);
    const valid = [];
    for (const f of fl) {
      if (!ALLOWED.includes(f.type)) { toast.error(`${f.name}: tipo non consentito`); continue; }
      if (f.size > MAX_SIZE) { toast.error(`${f.name}: troppo grande (>10MB)`); continue; }
      valid.push(f);
    }
    if ((files.length + valid.length) > 5) { toast.error("Massimo 5 file"); }
    setFiles([...files, ...valid].slice(0, 5));
    if (inputRef.current) inputRef.current.value = "";
  };
  const remove = (i) => setFiles(files.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" onChange={onPick} className="hidden" data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid={`${testId}-btn`}>
        <Paperclip size={14} className="mr-2" />Allega file
      </Button>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => (
            <div key={i} className="relative bg-[#F4F7FB] border border-[#0D1F3C]/8 rounded-lg p-2 flex items-center gap-2 text-xs" data-testid={`${testId}-chip-${i}`}>
              {f.type.startsWith("image/") ? (
                <img src={URL.createObjectURL(f)} alt={f.name} className="w-10 h-10 object-cover rounded" />
              ) : (
                <FilePdf size={28} className="text-red-600" />
              )}
              <div className="max-w-[140px]">
                <div className="font-medium truncate">{f.name}</div>
                <div className="text-[#0D1F3C]/50">{formatSize(f.size)}</div>
              </div>
              <button onClick={() => remove(i)} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center" data-testid={`${testId}-remove-${i}`}>
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AttachmentList({ ticketId, attachments }) {
  if (!attachments?.length) return null;
  const url = (a) => `${API}/tickets/${ticketId}/attachments/${a.storage_id}`;
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid="attachment-list">
      {attachments.map((a, i) => {
        const isImage = a.content_type?.startsWith("image/");
        return isImage ? (
          <a key={i} href={url(a)} target="_blank" rel="noopener noreferrer"
             className="block w-[120px] h-[120px] rounded-lg overflow-hidden border border-[#0D1F3C]/8 hover:border-[#1B4FD8] transition"
             data-testid={`attachment-img-${i}`} title={a.filename}>
            <img src={url(a)} alt={a.filename} className="w-full h-full object-cover" />
          </a>
        ) : (
          <a key={i} href={url(a)} download={a.filename}
             className="flex items-center gap-3 bg-white border border-[#0D1F3C]/8 hover:border-[#1B4FD8] rounded-lg p-3 text-xs transition min-w-[180px]"
             data-testid={`attachment-pdf-${i}`}>
            <FilePdf size={28} className="text-red-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#0D1F3C] truncate">{a.filename}</div>
              <div className="text-[#0D1F3C]/50">{formatSize(a.size_bytes)}</div>
            </div>
            <DownloadSimple size={16} className="text-[#1B4FD8] shrink-0" />
          </a>
        );
      })}
    </div>
  );
}

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [newFiles, setNewFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tickets`, { withCredentials: true });
      setTickets(data || []);
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Compila subject e messaggio"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("subject", subject); fd.append("message", message); fd.append("priority", priority);
      newFiles.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/tickets`, fd, { withCredentials: true });
      toast.success("Ticket aperto. Riceverai una risposta via email.");
      setShowNew(false); setSubject(""); setMessage(""); setPriority("normal"); setNewFiles([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const openTicket = async (id) => {
    try {
      const { data } = await axios.get(`${API}/tickets/${id}`, { withCredentials: true });
      setThread(data); setSelected(id); setReply(""); setReplyFiles([]);
    } catch { toast.error("Errore"); }
  };

  const handleReply = async () => {
    if (!reply.trim() && replyFiles.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("body", reply);
      replyFiles.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/tickets/${selected}/messages`, fd, { withCredentials: true });
      toast.success("Messaggio inviato"); setReply(""); setReplyFiles([]);
      await openTicket(selected); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="support-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0D1F3C]">Supporto</h1>
          <p className="text-sm text-[#0D1F3C]/60 mt-1">Apri un ticket, riceverai la risposta via email.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="support-new-ticket-btn">
          <Plus size={16} className="mr-2" />Apri nuovo ticket
        </Button>
      </div>

      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl overflow-hidden">
        <div className="p-4 bg-[#F4F7FB] border-b border-[#0D1F3C]/8">
          <h3 className="font-semibold text-[#0D1F3C]">I miei ticket</h3>
        </div>
        {loading ? (
          <div className="text-center py-8 text-[#0D1F3C]/40">Caricamento…</div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 text-[#0D1F3C]/40">
            Nessun ticket. <button className="text-[#1B4FD8] underline" onClick={() => setShowNew(true)}>Apri il primo</button>
          </div>
        ) : (
          <div className="divide-y divide-[#0D1F3C]/8">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => openTicket(t.id)}
                data-testid={`support-ticket-${t.id}`}
                className="w-full text-left px-4 py-3 hover:bg-[#F4F7FB] flex items-center justify-between">
                <div>
                  <div className="font-medium text-[#0D1F3C]">{t.subject}</div>
                  <div className="text-xs text-[#0D1F3C]/60 mt-0.5">
                    Aperto {new Date(t.created_at).toLocaleDateString("it-IT")} · {t.messages?.length || 0} messaggi
                    {(t.attachments?.length > 0 || t.messages?.some((m) => m.attachments?.length > 0)) && <> · 📎 con allegati</>}
                  </div>
                </div>
                <Badge className={STATUS_COLOR[t.status]}>{STATUS_LABEL[t.status] || t.status}</Badge>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="support-new-ticket-dialog">
          <DialogHeader><DialogTitle>Apri nuovo ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Oggetto</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Riassunto breve" data-testid="support-new-subject" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Messaggio</label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Descrivi il problema..." rows={5} data-testid="support-new-message" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Priorità</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="support-new-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Allegati (opzionali — max 5, 10MB cad)</label>
              <FilePicker files={newFiles} setFiles={setNewFiles} testId="support-new-files" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNew(false)} disabled={busy}>Annulla</Button>
              <Button onClick={handleCreate} disabled={busy} data-testid="support-new-submit">Apri ticket</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setThread(null))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="support-thread-dialog">
          <DialogHeader>
            <DialogTitle>{thread?.subject || "Ticket"}</DialogTitle>
            <div className="flex items-center gap-2 mt-1">
              {thread && <Badge className={STATUS_COLOR[thread.status]}>{STATUS_LABEL[thread.status] || thread.status}</Badge>}
            </div>
          </DialogHeader>
          {thread && (
            <div className="space-y-3">
              <div className="bg-[#F4F7FB] rounded-lg p-4">
                <div className="text-xs text-[#0D1F3C]/60 mb-1">Tu · {new Date(thread.created_at).toLocaleString("it-IT")}</div>
                <div className="text-sm whitespace-pre-wrap">{thread.body}</div>
                <AttachmentList ticketId={thread.id} attachments={thread.attachments} />
              </div>
              {(thread.messages || []).map((m) => (
                <div key={m.id} className={`rounded-lg p-4 ${m.from_admin_id ? "bg-blue-50 mr-8" : "bg-[#F4F7FB] ml-8"}`}>
                  <div className="text-xs text-[#0D1F3C]/60 mb-1">
                    {m.from_admin_id ? "Supporto SELLY" : "Tu"} · {new Date(m.created_at).toLocaleString("it-IT")}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  <AttachmentList ticketId={thread.id} attachments={m.attachments} />
                </div>
              ))}
              <div className="border-t border-[#0D1F3C]/8 pt-3 space-y-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Aggiungi un messaggio..." rows={3} data-testid="support-thread-reply" />
                <FilePicker files={replyFiles} setFiles={setReplyFiles} testId="support-reply-files" />
                <div className="flex justify-end">
                  <Button onClick={handleReply} disabled={busy || (!reply.trim() && replyFiles.length === 0)} data-testid="support-thread-send">
                    <PaperPlaneRight size={14} className="mr-2" />Invia
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
